/** The accounting depreciates over five years. */
export const AFSCHRIJF_MAANDEN = 60;
/** Fallback residual share when the accounting's own figure is unknown. */
const RESTWAARDE_DEEL = 0.2;
/**
 * Purchase price the depreciation runs from, falling back to the catalogue price.
 */
export function afschrijfBasis(car) {
    return car.aanschafwaarde || car.catalogusprijs || 0;
}
/**
 * Residual value at the end of the term.
 *
 * Uses the accounting's own figure when we have it, and only falls back to 20% of
 * the purchase price otherwise. That fallback is right for most of the fleet but
 * not a rule: checked against the asset cards, the nineteen cars capitalised from
 * 2024 onwards sit at exactly 20%, while the four from 2022/2023 carry a flat
 * EUR 4.500 — 21% for the Corolla up to 34% for the oldest Polo. Assuming 20%
 * there depreciated G382XB at EUR 179,26 a month against the EUR 149,08 actually
 * booked, and put its book value 22% under the ledger's.
 */
export function eindwaardeVan(car) {
    if (car.eindwaarde != null && car.eindwaarde >= 0)
        return car.eindwaarde;
    return afschrijfBasis(car) * RESTWAARDE_DEEL;
}
/**
 * Depreciation of the asset per month: purchase price down to the residual,
 * straight-line over 60 months. 0 when there is nothing to depreciate.
 *
 * This is the asset's depreciation, regardless of who owns it. Whether it is a
 * cost *of ours* is a separate question that `afschrijvingPerMaand` in `tco.ts`
 * answers — with lease or a loaner it sits inside the monthly instalment.
 *
 * Verified against grootboek 5120: for 22 of the 23 owned cars this lands on the
 * booked amount to the cent, the exception being one car with a part-month in its
 * first period.
 */
export function afschrijving(car) {
    const koop = afschrijfBasis(car);
    if (!koop || !car.aanschafdatum)
        return 0;
    return Math.max(0, koop - eindwaardeVan(car)) / AFSCHRIJF_MAANDEN;
}
/**
 * Book value at `asOf`: purchase price minus the depreciation booked so far,
 * never below the residual.
 *
 * **The purchase month counts as the first depreciated month.** That is how the
 * accounting does it — GBV86V, bought July 2024, has 25 months booked by July
 * 2026, not 24 — and it is what `afschrijfMaandenIn` in the TCO already assumed,
 * so counting elapsed months here left the card and the chart one month apart on
 * every owned car.
 */
export function restwaarde(car, asOf = new Date()) {
    const koop = afschrijfBasis(car);
    if (!koop || !car.aanschafdatum)
        return null;
    const d = new Date(car.aanschafdatum);
    if (Number.isNaN(d.getTime()))
        return null;
    const verstreken = (asOf.getFullYear() - d.getFullYear()) * 12 + (asOf.getMonth() - d.getMonth());
    const maanden = Math.min(AFSCHRIJF_MAANDEN, Math.max(0, verstreken + 1));
    return Math.round(koop - afschrijving(car) * maanden);
}
//# sourceMappingURL=depreciation.js.map